import { supabase } from '../../providers/supabase/client';

// ============================================
// Invoice CRUD Operations
// ============================================

export const fetchInvoices = async () => {
  return await supabase
    .from('invoices')
    .select(`
      *,
      invoice_items (*)
    `)
    .order('created_at', { ascending: false });
};

export const fetchInvoicesByCategory = async (category: string) => {
  return await supabase
    .from('invoices')
    .select(`
      *,
      invoice_items (*)
    `)
    .eq('category', category)
    .order('created_at', { ascending: false });
};

export const fetchInvoiceById = async (id: string) => {
  return await supabase
    .from('invoices')
    .select(`
      *,
      invoice_items (*)
    `)
    .eq('id', id)
    .single();
};

export const createInvoice = async (invoice: any) => {
  return await supabase
    .from('invoices')
    .insert([invoice])
    .select()
    .single();
};

export const updateInvoice = async (id: string, data: any) => {
  return await supabase
    .from('invoices')
    .update(data)
    .eq('id', id)
    .select()
    .single();
};

export const deleteInvoice = async (id: string) => {
  return await supabase.from('invoices').delete().eq('id', id);
};

export const fetchInvoicesByReservationId = async (reservationId: string) => {
  return await supabase
    .from('invoices')
    .select('*')
    .eq('reservation_id', reservationId)
    .eq('flow_type', 'VENTA')
    .order('created_at', { ascending: false });
};

export const fetchInvoicesByApOrderIds = async (apOrderIds: string[]) => {
  return await supabase
    .from('invoices')
    .select('id, ap_order_id, document_number, document_type, file_url, supplier_name, total, currency')
    .in('ap_order_id', apOrderIds)
    .order('created_at', { ascending: true });
};

export const fetchPaymentsForTransactions = async () => {
  return await supabase
    .from('payments')
    .select(`
      id,
      amount,
      amount_pen,
      payment_exchange_rate,
      paid_at,
      status,
      reservation_id,
      receipt_url,
      reservations (
        id,
        clients ( name, first_name, last_name )
      ),
      invoices (
        id, document_type, document_number, total, file_url, currency, status, sunat_status,
        flow_type, supplier_ruc, supplier_name, issue_date, created_at, op_gravada, op_inafecta, op_exonerada, igv, other_taxes, payment_id, ap_order_id
      )
    `)
    .order('paid_at', { ascending: false });
};

export const fetchApOrdersForTransactions = async () => {
  return await supabase
    .from('ap_payment_orders')
    .select(`
      id,
      requested_amount,
      real_paid_amount,
      requested_currency,
      status,
      created_at,
      reservation_id,
      receipt_url,
      concept_description,
      providers ( name ),
      invoices (
        id, document_type, document_number, total, file_url, currency, status, sunat_status,
        flow_type, supplier_ruc, supplier_name, issue_date, created_at, op_gravada, op_inafecta, op_exonerada, igv, other_taxes, payment_id, ap_order_id
      )
    `)
    .order('created_at', { ascending: false });
};

export const fetchStandaloneInvoices = async () => {
  return await supabase
    .from('invoices')
    .select(`
      id,
      flow_type,
      total,
      currency,
      created_at,
      status,
      supplier_name,
      supplier_ruc,
      issue_date,
      op_gravada,
      op_inafecta,
      op_exonerada,
      igv,
      other_taxes,
      reservation_id,
      document_type,
      document_number,
      file_url,
      payment_receipt_url,
      sunat_status,
      payment_id,
      ap_order_id,
      clients ( name, first_name, last_name )
    `)
    .is('payment_id', null)
    .is('ap_order_id', null)
    .order('created_at', { ascending: false });
};

export const fetchTreasuryTransactionsForDecoupledMapping = async () => {
  return await supabase
    .from('treasury_transactions')
    .select('reference_invoice_id, reference_ap_order_id, transaction_date');
};

// ============================================
// Invoice Items Operations
// ============================================

export const createInvoiceItems = async (items: any[]) => {
  return await supabase
    .from('invoice_items')
    .insert(items)
    .select();
};

export const updateInvoiceItem = async (id: string, data: any) => {
  return await supabase
    .from('invoice_items')
    .update(data)
    .eq('id', id)
    .select()
    .single();
};

export const deleteInvoiceItem = async (id: string) => {
  return await supabase.from('invoice_items').delete().eq('id', id);
};

export const deleteInvoiceItemsByInvoiceId = async (invoiceId: string) => {
  return await supabase
    .from('invoice_items')
    .delete()
    .eq('invoice_id', invoiceId);
};

// ============================================
// File Storage Operations
// ============================================

export const uploadInvoiceFile = async (file: File, userId: string) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

  const { data, error } = await supabase.storage
    .from('invoices')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;

  const { data: urlData } = supabase.storage
    .from('invoices')
    .getPublicUrl(data.path);

  return {
    path: data.path,
    url: urlData.publicUrl,
    fileName: file.name,
  };
};

export const deleteInvoiceFile = async (filePath: string) => {
  return await supabase.storage.from('invoices').remove([filePath]);
};

// ============================================
// OCR Processing
// ============================================

export const processInvoiceOCR = async (fileUrl: string | null, fileName: string, fileBase64?: string, flow_type?: string): Promise<any> => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) throw new Error('No authenticated session');

  const payload: any = { fileName };
  if (fileUrl) payload.fileUrl = fileUrl;
  if (fileBase64) payload.fileBase64 = fileBase64;
  if (flow_type) payload.flow_type = flow_type;

  const response = await fetch(`${supabaseUrl}/functions/v1/process-invoice`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Edge Function Error:', errorText);
    throw new Error(`Error ${response.status}: ${errorText}`);
  }

  const result = await response.json();
  return result;
};

// ============================================
// Supplier Search (autocomplete from history)
// ============================================

export const searchSupplierByRuc = async (ruc: string) => {
  const { data } = await supabase
    .from('invoices')
    .select('supplier_name, supplier_ruc')
    .eq('supplier_ruc', ruc)
    .limit(1)
    .single();

  return data;
};

export const searchSuppliers = async (query: string) => {
  const { data } = await supabase
    .from('invoices')
    .select('supplier_name, supplier_ruc')
    .or(`supplier_name.ilike.%${query}%,supplier_ruc.ilike.%${query}%`)
    .limit(10);

  // Deduplicate by RUC
  const seen = new Set();
  return (data || []).filter((item: any) => {
    if (seen.has(item.supplier_ruc)) return false;
    seen.add(item.supplier_ruc);
    return true;
  });
};

// ============================================
// Recurring Expenses Operations
// ============================================

export const fetchRecurringExpenses = async () => {
  return await supabase
    .from('recurring_expenses')
    .select('*, providers(id, name, ruc)')
    .order('next_due_date', { ascending: true });
};

export const createRecurringExpense = async (expense: any) => {
  return await supabase
    .from('recurring_expenses')
    .insert([expense])
    .select();
};

export const updateRecurringExpense = async (id: string, data: any) => {
  return await supabase
    .from('recurring_expenses')
    .update(data)
    .eq('id', id)
    .select()
    .single();
};

export const deleteRecurringExpense = async (id: string) => {
  return await supabase.from('recurring_expenses').delete().eq('id', id);
};

// ============================================
// Chart of Accounts (PCGE)
// ============================================

export const fetchChartOfAccounts = async () => {
  return await supabase
    .from('chart_of_accounts')
    .select('*')
    .eq('is_active', true)
    .order('code', { ascending: true });
};

export const fetchAnalyticalAccounts = async () => {
  return await supabase
    .from('chart_of_accounts')
    .select('*')
    .eq('is_active', true)
    .eq('is_analytical', true)
    .order('code', { ascending: true });
};

// ============================================
// Journal Entries (Libro Diario)
// ============================================

export const fetchJournalEntries = async (period?: string) => {
  let query = supabase
    .from('journal_entries')
    .select(`
      *,
      journal_lines (*)
    `)
    .order('entry_date', { ascending: false })
    .order('entry_number', { ascending: false });

  if (period) {
    query = query.eq('period', period);
  }

  return await query;
};

export const fetchJournalEntryById = async (id: string) => {
  return await supabase
    .from('journal_entries')
    .select(`
      *,
      journal_lines (
        *,
        chart_of_accounts:account_code (code, name)
      )
    `)
    .eq('id', id)
    .single();
};

export const createJournalEntry = async (entry: any) => {
  return await supabase
    .from('journal_entries')
    .insert([entry])
    .select()
    .single();
};

export const updateJournalEntry = async (id: string, entry: any) => {
  return await supabase
    .from('journal_entries')
    .update(entry)
    .eq('id', id)
    .select()
    .single();
};

export const createJournalLines = async (lines: any[]) => {
  return await supabase
    .from('journal_lines')
    .insert(lines)
    .select();
};

/**
 * Reverse a journal entry (asiento de reversión).
 * Creates a new entry with debits ↔ credits swapped, and marks the original as REVERSED.
 * This is the standard accounting practice — entries are never deleted.
 */
export const reverseJournalEntry = async (entryId: string) => {
  // 1. Fetch original entry + lines
  const { data: original, error: fetchErr } = await supabase
    .from('journal_entries')
    .select('*, journal_lines(*)')
    .eq('id', entryId)
    .single();

  if (fetchErr || !original) throw fetchErr || new Error('Asiento no encontrado');
  if (original.status === 'REVERSED') throw new Error('Este asiento ya fue reversado');

  const { data: { user } } = await supabase.auth.getUser();

  // 2. Create the reversal entry
  const today = new Date().toISOString().split('T')[0];
  const period = today.substring(0, 7); // YYYY-MM

  const { data: reversalEntry, error: createErr } = await supabase
    .from('journal_entries')
    .insert({
      entry_date: today,
      gloss: `REVERSIÓN: ${original.gloss}`,
      reference_type: original.reference_type,
      reference_id: original.reference_id,
      period,
      status: 'POSTED',
      created_by: user?.id,
    })
    .select()
    .single();

  if (createErr || !reversalEntry) throw createErr || new Error('Error al crear asiento de reversión');

  // 3. Create reversal lines (swap debit ↔ credit)
  const originalLines = original.journal_lines || [];
  const reversalLines = originalLines.map((line: any, idx: number) => ({
    entry_id: reversalEntry.id,
    account_code: line.account_code,
    description: `(Rev.) ${line.description || ''}`,
    debit: parseFloat(line.credit) || 0,   // Credit → Debit
    credit: parseFloat(line.debit) || 0,   // Debit → Credit
    cost_center: line.cost_center,
    currency: line.currency,
    exchange_rate: line.exchange_rate,
    amount_original: line.amount_original ? -parseFloat(line.amount_original) : null,
    sort_order: idx,
  }));

  if (reversalLines.length > 0) {
    const { error: linesErr } = await supabase
      .from('journal_lines')
      .insert(reversalLines);
    if (linesErr) throw linesErr;
  }

  // 4. Mark original entry as REVERSED
  const { error: updateErr } = await supabase
    .from('journal_entries')
    .update({ status: 'REVERSED' })
    .eq('id', entryId);

  if (updateErr) throw updateErr;

  return reversalEntry;
};

// ============================================
// Accounting Rules
// ============================================

export const fetchAccountingRules = async () => {
  return await supabase
    .from('accounting_rules')
    .select('*')
    .eq('is_active', true)
    .order('trigger_module', { ascending: true });
};

// ============================================
// Exchange Rates
// ============================================

export const fetchExchangeRates = async (limit = 30) => {
  return await supabase
    .from('exchange_rates')
    .select('*')
    .order('rate_date', { ascending: false })
    .limit(limit);
};

export const upsertExchangeRate = async (rate: { rate_date: string; buy_rate: number; sell_rate: number; source?: string }) => {
  return await supabase
    .from('exchange_rates')
    .upsert([rate], { onConflict: 'rate_date' })
    .select()
    .single();
};

// ============================================
// Auto-Journaling: Creates double-entry entries
// ============================================

/**
 * Generate a journal entry for a confirmed purchase invoice.
 * Debit: Expense account (from accounting_rules based on category)
 * Debit: IGV Credit (40112) if applicable
 * Credit: Accounts Payable (4212)
 */
export const generateInvoiceJournalEntry = async (invoice: any) => {
  const isVenta = invoice.flow_type === 'VENTA';
  const triggerModule = isVenta ? 'VENTAS' : 'COMPRAS';
  const triggerEvent = isVenta ? 'INVOICE_EMITTED' : 'INVOICE_CONFIRMED';
  const category = isVenta ? 'venta_paquete' : (invoice.category || 'gasto_operativo');

  // 1. Find the matching accounting rule
  const { data: rules } = await supabase
    .from('accounting_rules')
    .select('*')
    .eq('trigger_module', triggerModule)
    .eq('trigger_event', triggerEvent)
    .eq('category', category)
    .eq('is_active', true)
    .limit(1);

  const rule = rules?.[0];
  if (!rule) {
    console.warn('[Auto-Journal] No accounting rule found for:', { triggerModule, triggerEvent, category });
    return null;
  }

  const { data: { user } } = await supabase.auth.getUser();
  const period = invoice.issue_date ? invoice.issue_date.substring(0, 7) : new Date().toISOString().substring(0, 7);
  
  // 2. Build the gloss from the template
  const gloss = (rule.description_template || (isVenta ? 'Venta' : 'Compra'))
    .replace('{supplier_name}', invoice.supplier_name || '')
    .replace('{client_name}', invoice.supplier_name || '') // For sales, supplier_name holds client name
    .replace('{document_number}', invoice.document_number || '');

  // 3. Create the journal entry header
  const { data: entry, error: entryError } = await createJournalEntry({
    entry_date: invoice.issue_date || new Date().toISOString().split('T')[0],
    gloss,
    reference_type: isVenta ? 'SALE' : 'INVOICE',
    reference_id: invoice.id,
    period,
    status: 'POSTED',
    created_by: user?.id,
  });

  if (entryError || !entry) {
    console.error('[Auto-Journal] Failed to create entry:', entryError);
    return null;
  }

  // 4. Build journal lines — Fetch SUNAT exchange rate for the invoice date
  const invoiceCurrency = invoice.currency || (isVenta ? 'USD' : 'PEN');
  let tcVenta = 1; // default for PEN
  if (invoiceCurrency === 'USD') {
    const invoiceDate = invoice.issue_date || new Date().toISOString().split('T')[0];
    // Try exact date first, then fallback to most recent previous date
    const { data: rateExact } = await supabase
      .from('exchange_rates')
      .select('sell_rate')
      .eq('rate_date', invoiceDate)
      .maybeSingle();
    
    if (rateExact?.sell_rate) {
      tcVenta = parseFloat(rateExact.sell_rate);
    } else {
      // Fallback: most recent rate before this date (SUNAT rule)
      const { data: rateFallback } = await supabase
        .from('exchange_rates')
        .select('sell_rate')
        .lt('rate_date', invoiceDate)
        .order('rate_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (rateFallback?.sell_rate) {
        tcVenta = parseFloat(rateFallback.sell_rate);
      }
    }
    console.log(`[Auto-Journal] TC venta SUNAT for ${invoiceDate}: ${tcVenta}`);
  }

  const lines: any[] = [];
  const baseAmount = parseFloat(invoice.op_gravada || 0) + parseFloat(invoice.op_inafecta || 0) + parseFloat(invoice.op_exonerada || 0) + parseFloat(invoice.op_exportacion || 0);
  const igvAmount = parseFloat(invoice.igv || 0);
  const otherTaxesAmount = parseFloat(invoice.other_taxes || 0);
  const totalAmount = parseFloat(invoice.total || 0);

  if (isVenta) {
    // VENTA: Debit Cuentas por Cobrar, Credit Ingresos (or Deferred Revenue) + IGV + Otros Tributos
    // NIIF 15 Rule: If the travel starts in the future, credit 122 (Anticipos - Pasivo) instead of 7041 (Ventas)
    const hasFutureTravel = invoice.travel_date && new Date(invoice.travel_date) > new Date(invoice.issue_date || new Date());
    const creditAccount = hasFutureTravel ? '122' : (rule.credit_account || '7041');
    const creditDesc = hasFutureTravel ? `Anticipo de Cliente (NIIF 15): ${invoice.supplier_name || ''}` : `Ingreso: ${invoice.supplier_name || ''}`;

    // Line 1: Debit 1212 (Cuentas por cobrar) — total
    lines.push({
      entry_id: entry.id,
      account_code: rule.debit_account || '1212',
      description: gloss,
      debit: totalAmount,
      credit: 0,
      currency: invoiceCurrency,
      exchange_rate: tcVenta,
      amount_original: totalAmount,
      sort_order: 1,
    });

    // Line 2: Credit 122 (Deferred) or 7041 (Revenue) — base
    if (baseAmount > 0) {
      lines.push({
        entry_id: entry.id,
        account_code: creditAccount,
        description: creditDesc,
        debit: 0,
        credit: baseAmount,
        currency: invoiceCurrency,
        exchange_rate: tcVenta,
        amount_original: baseAmount,
        sort_order: 2,
      });
    }

    // Line 3: Credit 40111 (IGV Débito fiscal) — if IGV > 0
    if (igvAmount > 0) {
      lines.push({
        entry_id: entry.id,
        account_code: '40111',
        description: `IGV Débito: ${invoice.document_number || ''}`,
        debit: 0,
        credit: igvAmount,
        currency: invoiceCurrency,
        exchange_rate: tcVenta,
        amount_original: igvAmount,
        sort_order: 3,
      });
    }
    
    // Line 4: Credit 40114 (Otros tributos) — if other_taxes > 0
    if (otherTaxesAmount > 0) {
      lines.push({
        entry_id: entry.id,
        account_code: '40114',
        description: `Otros Tributos: ${invoice.document_number || ''}`,
        debit: 0,
        credit: otherTaxesAmount,
        currency: invoiceCurrency,
        exchange_rate: tcVenta,
        amount_original: otherTaxesAmount,
        sort_order: 4,
      });
    }
  } else {
    // COMPRA: Debit Gasto + IGV Crédito + Otros Tributos, Credit Cuentas por Pagar
    if (invoice.is_third_party_document) {
      // Third Party Document: Debit bridge account 122 (Anticipos - Pasivo) directly for the total amount
      // This bypasses the agency's P&L and IGV credit book completely.
      lines.push({
        entry_id: entry.id,
        account_code: '122',
        description: `Reembolso Gasto Terceros: ${invoice.supplier_name || ''}`,
        debit: totalAmount,
        credit: 0,
        currency: invoiceCurrency,
        exchange_rate: tcVenta,
        amount_original: totalAmount,
        sort_order: 1,
      });

      lines.push({
        entry_id: entry.id,
        account_code: rule.credit_account || '4212',
        description: `Por pagar: ${invoice.supplier_name || ''}`,
        debit: 0,
        credit: totalAmount,
        currency: invoiceCurrency,
        exchange_rate: tcVenta,
        amount_original: totalAmount,
        sort_order: 2,
      });
    } else {
      // Line 1: Debit expense account (base imponible)
      if (baseAmount > 0) {
        lines.push({
          entry_id: entry.id,
          account_code: rule.debit_account,
          description: gloss,
          debit: baseAmount,
          credit: 0,
          currency: invoiceCurrency,
          exchange_rate: tcVenta,
          amount_original: baseAmount,
          sort_order: 1,
        });
      }

      // Line 2: Debit IGV credit fiscal (if IGV > 0)
      if (igvAmount > 0) {
        lines.push({
          entry_id: entry.id,
          account_code: '40112', // IGV por aplicar
          description: `IGV Crédito: ${invoice.document_number || ''}`,
          debit: igvAmount,
          credit: 0,
          currency: invoiceCurrency,
          exchange_rate: tcVenta,
          amount_original: igvAmount,
          sort_order: 2,
        });
      }
      
      // Line 3: Debit Otros Tributos (6419) if other_taxes > 0
      if (otherTaxesAmount > 0) {
        lines.push({
          entry_id: entry.id,
          account_code: '6419', // Otros Gastos por Tributos
          description: `Otros Tributos: ${invoice.document_number || ''}`,
          debit: otherTaxesAmount,
          credit: 0,
          currency: invoiceCurrency,
          exchange_rate: tcVenta,
          amount_original: otherTaxesAmount,
          sort_order: 3,
        });
      }

      // Line 4: Credit accounts payable (total)
      lines.push({
        entry_id: entry.id,
        account_code: rule.credit_account || '4212',
        description: `Por pagar: ${invoice.supplier_name || ''}`,
        debit: 0,
        credit: totalAmount,
        currency: invoiceCurrency,
        exchange_rate: tcVenta,
        amount_original: totalAmount,
        sort_order: 4,
      });
    }
  }

  const { error: linesError } = await createJournalLines(lines);
  if (linesError) {
    console.error('[Auto-Journal] Failed to create lines:', linesError);
    return null;
  }

  // 5. Link the journal entry back to the invoice
  await supabase
    .from('invoices')
    .update({ journal_entry_id: entry.id })
    .eq('id', invoice.id);

  return entry;
};

/**
 * Generate a journal entry for a verified client payment.
 * Debits Bank account (1041) at the Commercial Exchange Rate (actual Soles paid).
 * Credits Receivable (1212) at the official SUNAT Exchange Rate.
 * Debits/Credits Difference of Exchange (676 or 776).
 */
export const generatePaymentJournalEntry = async (payment: any) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    // 1. Fetch linked treasury transaction to get bank account, actual Soles paid, and TC Comercial
    const { data: tx, error: txError } = await supabase
      .from('treasury_transactions')
      .select(`
        *,
        bank_accounts ( id, name, currency )
      `)
      .eq('reference_client_payment_id', payment.id)
      .maybeSingle();

    if (txError || !tx) {
      console.warn('[Auto-Journal-Payment] No linked treasury transaction found for payment:', payment.id);
      return null;
    }

    // 2. Fetch linked sales invoice to get RUC/Name, total, and travel date
    const { data: invoice } = await supabase
      .from('invoices')
      .select('*')
      .eq('payment_id', payment.id)
      .maybeSingle();

    const clientName = invoice?.supplier_name || 'Cliente';
    const documentNumber = invoice?.document_number || '';
    const paymentDate = payment.paid_at ? payment.paid_at.substring(0, 10) : new Date().toISOString().split('T')[0];
    const period = paymentDate.substring(0, 7);

    // 3. Retrieve SUNAT exchange rate for the payment/invoice date
    let tcSunat = 1;
    const { data: rateExact } = await supabase
      .from('exchange_rates')
      .select('sell_rate')
      .eq('rate_date', paymentDate)
      .maybeSingle();

    if (rateExact?.sell_rate) {
      tcSunat = parseFloat(rateExact.sell_rate);
    } else {
      const { data: rateFallback } = await supabase
        .from('exchange_rates')
        .select('sell_rate')
        .lt('rate_date', paymentDate)
        .order('rate_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (rateFallback?.sell_rate) {
        tcSunat = parseFloat(rateFallback.sell_rate);
      } else {
        tcSunat = 3.750; // safe fallback
      }
    }

    const tcComer = parseFloat(tx.exchange_rate) || tcSunat;

    // Actual currency of transaction
    const paymentCurrency = payment.currency || invoice?.currency || 'USD';
    const totalUSD = parseFloat(payment.amount);
    
    // If client paid in PEN directly, use that amount; otherwise use treasury tx amount
    const isPenPayment = payment.amount_pen && Number(payment.amount_pen) > 0;
    const solesRecibidos = isPenPayment 
        ? Number(payment.amount_pen) 
        : parseFloat(tx.amount || 0);
    // Accounts Receivable credit valued at the official SUNAT rate
    const receivableSoles = totalUSD * tcSunat;

    const difference = solesRecibidos - receivableSoles;
    const gloss = `Cobro Cliente: ${clientName} - Pago: ${documentNumber || 'Ref'}`;

    // Create journal entry header
    const { data: entry, error: entryError } = await createJournalEntry({
      entry_date: paymentDate,
      gloss,
      reference_type: 'PAYMENT',
      reference_id: payment.id,
      period,
      status: 'POSTED',
      created_by: user?.id,
    });

    if (entryError || !entry) {
      console.error('[Auto-Journal-Payment] Failed to create payment entry:', entryError);
      return null;
    }

    const lines = [];

    // Line 1: Debit 1041 (Caja y Bancos) - Actual cash received (Soles)
    lines.push({
      entry_id: entry.id,
      account_code: '1041',
      description: `Ingreso Caja/Bancos: ${tx.bank_accounts?.name || 'Bancos'}`,
      debit: solesRecibidos,
      credit: 0,
      currency: paymentCurrency,
      exchange_rate: tcComer,
      amount_original: totalUSD,
      sort_order: 1,
    });

    // Line 2: Credit 1212 (Cuentas por Cobrar Comerciales) - Total receivable at TC SUNAT
    lines.push({
      entry_id: entry.id,
      account_code: '1212',
      description: `Cancelación Deuda - ${clientName}`,
      debit: 0,
      credit: receivableSoles,
      currency: paymentCurrency,
      exchange_rate: tcSunat,
      amount_original: totalUSD,
      sort_order: 2,
    });

    // Line 3: Register Difference of Exchange
    if (Math.abs(difference) > 0.01) {
      if (difference > 0) {
        // Gain: Credit 776 (Ganancias por diferencia de cambio)
        lines.push({
          entry_id: entry.id,
          account_code: '776',
          description: 'Ganancia Cambiaria Comercial',
          debit: 0,
          credit: Math.abs(difference),
          currency: 'PEN',
          exchange_rate: 1.0000,
          amount_original: Math.abs(difference),
          sort_order: 3,
        });
      } else {
        // Loss: Debit 676 (Pérdidas por diferencia de cambio)
        lines.push({
          entry_id: entry.id,
          account_code: '676',
          description: 'Pérdida Cambiaria Comercial',
          debit: Math.abs(difference),
          credit: 0,
          currency: 'PEN',
          exchange_rate: 1.0000,
          amount_original: Math.abs(difference),
          sort_order: 3,
        });
      }
    }

    const { error: linesError } = await createJournalLines(lines);
    if (linesError) {
      console.error('[Auto-Journal-Payment] Failed to create payment lines:', linesError);
      return null;
    }

    // Link the journal entry back to the payment
    await supabase
      .from('payments')
      .update({ journal_entry_id: entry.id })
      .eq('id', payment.id);

    return entry;
  } catch (err) {
    console.error('[Auto-Journal-Payment] Unexpected error:', err);
    return null;
  }
};

// ============================================
// Treasury Transactions
// ============================================

export const createTreasuryTransaction = async (transaction: any) => {
  return await supabase.from('treasury_transactions').insert([transaction]);
};

export const fetchTransactionsByInvoiceId = async (invoiceId: string) => {
  return await supabase
    .from('treasury_transactions')
    .select('*')
    .eq('reference_invoice_id', invoiceId);
};

export const updateTreasuryTransaction = async (id: string, transaction: any) => {
  return await supabase
    .from('treasury_transactions')
    .update(transaction)
    .eq('id', id);
};

export const deleteTreasuryTransactionByInvoiceId = async (invoiceId: string) => {
  return await supabase
    .from('treasury_transactions')
    .delete()
    .eq('reference_invoice_id', invoiceId);
};

// ============================================
// IGV Summary & Monthly Closings
// ============================================

/**
 * Fetch ALL invoices for a given month (YYYY-MM)
 * for IGV summary calculations (PDT 621).
 * 
 * Includes invoices linked to payments (VENTA) and AP orders (COMPRA),
 * not just standalone invoices. Uses issue_date primarily, falls back 
 * to created_at when issue_date is null.
 */
export const fetchInvoicesByMonth = async (yearMonth: string) => {
  const startDate = `${yearMonth}-01`;
  const [y, m] = yearMonth.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const endDate = `${yearMonth}-${String(lastDay).padStart(2, '0')}`;

  // Use .or() to match by issue_date range OR created_at range (for invoices without issue_date)
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      id, flow_type, document_type, document_number, supplier_ruc, supplier_name, issue_date, created_at, op_gravada, op_inafecta, op_exonerada, igv, other_taxes, total, currency, status, payment_id, ap_order_id, client_id, reservation_id,
      clients ( id, name, first_name, last_name, document_type, document_number ),
      reservations (
        id,
        clients ( id, name, first_name, last_name, document_type, document_number )
      )
    `)
    .or(`and(issue_date.gte.${startDate},issue_date.lte.${endDate}),and(issue_date.is.null,created_at.gte.${startDate}T00:00:00,created_at.lte.${endDate}T23:59:59)`)
    .order('created_at', { ascending: true });

  if (error) {
    return { data: null, error };
  }

  // Normalize: ensure issue_date is always set for display/calculations
  const normalized = (data || []).map(inv => ({
    ...inv,
    issue_date: inv.issue_date || (inv.created_at ? inv.created_at.split('T')[0] : startDate),
  }));

  return { data: normalized, error: null };
};

/**
 * Fetch exchange rates for a given month to convert USD → PEN.
 * Uses sell_rate as per SUNAT Art. 5 Num. 17 TUO del IGV.
 */
export const fetchExchangeRatesByMonth = async (yearMonth: string) => {
  const startDate = `${yearMonth}-01`;
  const [y, m] = yearMonth.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const endDate = `${yearMonth}-${String(lastDay).padStart(2, '0')}`;

  return await supabase
    .from('exchange_rates')
    .select('rate_date, sell_rate')
    .gte('rate_date', startDate)
    .lte('rate_date', endDate)
    .order('rate_date', { ascending: true });
};

/**
 * Fetch the IGV monthly closing for a given period.
 */
export const fetchIgvClosing = async (period: string) => {
  return await supabase
    .from('igv_monthly_closings')
    .select('*')
    .eq('period', period)
    .maybeSingle();
};

/**
 * Save or update an IGV monthly closing (upsert by period).
 */
export const upsertIgvClosing = async (closing: any) => {
  return await supabase
    .from('igv_monthly_closings')
    .upsert(
      { ...closing, updated_at: new Date().toISOString() },
      { onConflict: 'period' }
    )
    .select()
    .single();
};

export const executeDevengadoProcess = async () => {
  return await supabase.rpc('process_monthly_devengado');
};

export const updateTransactionPaymentDate = async (id: string, type: 'INGRESO' | 'EGRESO', original: any, newDateStr: string, newAmount?: number) => {
  // Normalize date-only strings (YYYY-MM-DD) to noon to prevent timezone day-shift
  // when stored in timestamptz columns (e.g. '2026-05-25' at UTC midnight = May 24 in Peru UTC-5)
  const safeTxDate = newDateStr.length === 10 ? `${newDateStr}T12:00:00` : newDateStr;

  if (type === 'INGRESO') {
    if (original?.flow_type) {
      // Standalone manual sales invoice
      const { data: txRows, error: txErr } = await supabase
        .from('treasury_transactions')
        .update({ transaction_date: safeTxDate })
        .eq('reference_invoice_id', id)
        .select();
      if (txErr) {
        console.error("Error updating invoice treasury tx date:", txErr);
        throw txErr;
      }
      if (!txRows || txRows.length === 0) {
        // Fallback: Create missing treasury transaction dynamically
        const { data: invoice, error: getInvErr } = await supabase
          .from('invoices')
          .select('*')
          .eq('id', id)
          .single();
        if (getInvErr || !invoice) {
          throw new Error('No se pudo encontrar el comprobante para actualizar.');
        }

        let bankAccountId = invoice.paid_from_bank_account;
        if (!bankAccountId) {
          // Look up active bank account for matching currency
          const { data: accounts } = await supabase
            .from('bank_accounts')
            .select('id')
            .eq('currency', invoice.currency || 'PEN')
            .eq('is_active', true)
            .limit(1);
          if (accounts && accounts.length > 0) {
            bankAccountId = accounts[0].id;
          } else {
            // Fallback to any active bank account
            const { data: allAccounts } = await supabase
              .from('bank_accounts')
              .select('id')
              .eq('is_active', true)
              .limit(1);
            if (allAccounts && allAccounts.length > 0) {
              bankAccountId = allAccounts[0].id;
            }
          }
          if (bankAccountId) {
            await supabase
              .from('invoices')
              .update({ paid_from_bank_account: bankAccountId })
              .eq('id', id);
          }
        }

        if (bankAccountId) {
          const txDesc = 'Cobro de Venta: ' + (invoice.supplier_name || invoice.document_number || 'Manual');
          const { error: insertErr } = await supabase
            .from('treasury_transactions')
            .insert([{
              bank_account_id: bankAccountId,
              transaction_type: 'INBOUND',
              amount: invoice.total || 0,
              description: txDesc,
              reference_invoice_id: id,
              transaction_date: safeTxDate
            }]);
          if (insertErr) {
            console.error("Error creating missing treasury tx:", insertErr);
            throw insertErr;
          }
        } else {
          throw new Error('No se pudo actualizar la fecha de pago. No hay ninguna cuenta de banco activa en el sistema para asociar esta transacción.');
        }
      }
    } else {
      // Client payment (payments table)
      let sunatExchangeRate = null;
      try {
        const { data: exData } = await supabase
          .from("exchange_rates")
          .select("sell_rate")
          .eq("rate_date", newDateStr)
          .maybeSingle();

        if (exData && exData.sell_rate) {
          sunatExchangeRate = parseFloat(exData.sell_rate);
        } else {
          const { data: latestEx } = await supabase
            .from("exchange_rates")
            .select("sell_rate")
            .lt("rate_date", newDateStr)
            .order("rate_date", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (latestEx && latestEx.sell_rate) {
            sunatExchangeRate = parseFloat(latestEx.sell_rate);
          }
        }
      } catch (exErr) {
        console.warn("Failed to fetch SUNAT exchange rate for new date:", exErr);
      }

      // Update payments
      const { error: pErr } = await supabase
        .from('payments')
        .update({
          paid_at: new Date(safeTxDate).toISOString(),
          sunat_exchange_rate: sunatExchangeRate
        })
        .eq('id', id);
      if (pErr) throw pErr;

      // Update treasury transaction
      const { error: txErr } = await supabase
        .from('treasury_transactions')
        .update({
          transaction_date: safeTxDate
        })
        .eq('reference_client_payment_id', id);
      if (txErr) {
        console.error("Error updating treasury tx date:", txErr);
        throw txErr;
      }

      // Delete old journal entries (to be regenerated with new SUNAT rates/diff of exchange)
      try {
        const { data: entry } = await supabase
          .from('journal_entries')
          .select('id')
          .eq('reference_id', id)
          .eq('reference_type', 'PAYMENT')
          .maybeSingle();
        if (entry) {
          await supabase.from('journal_lines').delete().eq('entry_id', entry.id);
          await supabase.from('journal_entries').delete().eq('id', entry.id);
        }
      } catch (err) {
        console.error("Error deleting old payment journal entry:", err);
      }

      // Regenerate journal entry
      try {
        const { data: updatedPayment } = await supabase
          .from('payments')
          .select('*')
          .eq('id', id)
          .single();
        if (updatedPayment) {
          await generatePaymentJournalEntry(updatedPayment);
        }
      } catch (journalErr) {
        console.warn('[updatePaymentDate] Auto-journal payment entry failed (non-blocking):', journalErr);
      }
    }
  } else {
    // EGRESO
    if (original?.flow_type) {
      // Standalone manual purchase invoice
      if (newAmount !== undefined) {
        const { error: invErr } = await supabase
          .from('invoices')
          .update({
            total: newAmount,
            op_inafecta: newAmount
          })
          .eq('id', id);
        if (invErr) {
          console.error("Error updating invoice date/amount:", invErr);
          throw invErr;
        }
      }

      const txUpdate: any = { transaction_date: safeTxDate };
      if (newAmount !== undefined) {
        txUpdate.amount = newAmount;
      }

      const { data: txRows, error: txErr } = await supabase
        .from('treasury_transactions')
        .update(txUpdate)
        .eq('reference_invoice_id', id)
        .select();
      if (txErr) {
        console.error("Error updating invoice treasury tx date/amount:", txErr);
        throw txErr;
      }
      if (!txRows || txRows.length === 0) {
        // Fallback: Create missing treasury transaction dynamically
        const { data: invoice, error: getInvErr } = await supabase
          .from('invoices')
          .select('*')
          .eq('id', id)
          .single();
        if (getInvErr || !invoice) {
          throw new Error('No se pudo encontrar el comprobante para actualizar.');
        }

        let bankAccountId = invoice.paid_from_bank_account;
        if (!bankAccountId) {
          // Look up active bank account for matching currency
          const { data: accounts } = await supabase
            .from('bank_accounts')
            .select('id')
            .eq('currency', invoice.currency || 'PEN')
            .eq('is_active', true)
            .limit(1);
          if (accounts && accounts.length > 0) {
            bankAccountId = accounts[0].id;
          } else {
            // Fallback to any active bank account
            const { data: allAccounts } = await supabase
              .from('bank_accounts')
              .select('id')
              .eq('is_active', true)
              .limit(1);
            if (allAccounts && allAccounts.length > 0) {
              bankAccountId = allAccounts[0].id;
            }
          }
          if (bankAccountId) {
            await supabase
              .from('invoices')
              .update({ paid_from_bank_account: bankAccountId })
              .eq('id', id);
          }
        }

        if (bankAccountId) {
          const txDesc = 'Pago de Comprobante / Compra: ' + (invoice.supplier_name || invoice.document_number || 'Manual');
          const { error: insertErr } = await supabase
            .from('treasury_transactions')
            .insert([{
              bank_account_id: bankAccountId,
              transaction_type: 'OUTBOUND',
              amount: newAmount !== undefined ? newAmount : (invoice.total || 0),
              description: txDesc,
              reference_invoice_id: id,
              transaction_date: safeTxDate
            }]);
          if (insertErr) {
            console.error("Error creating missing treasury tx:", insertErr);
            throw insertErr;
          }
        } else {
          throw new Error('No se pudo actualizar la fecha de pago. No hay ninguna cuenta de banco activa en el sistema para asociar esta transacción.');
        }
      }
    } else {
      // B2B provider order (ap_payment_orders table)
      const { error: txErr } = await supabase
        .from('treasury_transactions')
        .update({
          transaction_date: safeTxDate
        })
        .eq('reference_ap_order_id', id);
      if (txErr) throw txErr;
    }
  }
};

export const deletePayment = async (id: string) => {
  // Delete linked treasury transaction first to preserve foreign key integrity
  const { error: txErr } = await supabase
    .from('treasury_transactions')
    .delete()
    .eq('reference_client_payment_id', id);
  if (txErr) console.error("Error deleting linked payment treasury transaction:", txErr);

  // Delete any linked journal lines and entries if they exist
  try {
    const { data: entry } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('reference_id', id)
      .eq('reference_type', 'PAYMENT')
      .maybeSingle();
    if (entry) {
      await supabase.from('journal_lines').delete().eq('entry_id', entry.id);
      await supabase.from('journal_entries').delete().eq('id', entry.id);
    }
  } catch (err) {
    console.error("Error deleting payment journal entry:", err);
  }

  // Delete payment itself
  return await supabase.from('payments').delete().eq('id', id);
};

export const updateTransactionVoucherUrl = async (id: string, type: 'INGRESO' | 'EGRESO', original: any, newUrl: string) => {
  if (original?.flow_type) {
    // Standalone manual invoice
    return await supabase
      .from('invoices')
      .update({ payment_receipt_url: newUrl })
      .eq('id', id);
  } else if (type === 'INGRESO') {
    // Client payment
    return await supabase
      .from('payments')
      .update({ receipt_url: newUrl })
      .eq('id', id);
  } else {
    // Provider payment order
    return await supabase
      .from('ap_payment_orders')
      .update({ receipt_url: newUrl })
      .eq('id', id);
  }
};

